import { useAuth0 } from '@auth0/auth0-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import useCompanyId from '@/Hooks/UseCompanyId';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

export type EmailProvider = 'microsoft' | 'google';
export type EmailConnectionStatus = 'Valid' | 'Invalid';

export interface EmailConnectionResponse {
    id: string;
    recruiterId: string;
    emailAddress: string;
    provider: EmailProvider;
    connectionStatus: EmailConnectionStatus;
    lastSyncedAt: string;
}

const DefaultEmailConnectionResponse: EmailConnectionResponse = {
    id: '',
    recruiterId: '',
    emailAddress: '',
    provider: 'google',
    connectionStatus: 'Invalid',
    lastSyncedAt: '',
};

const emailConnectionPath = (companyId: string): string => `company/${companyId}/email-connection`;

export const useEmailIntegrationStatus = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(emailConnectionPath(companyId), async (path) => {
        try {
            const response = await http.get<EmailConnectionResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response || DefaultEmailConnectionResponse;
        } catch (error) {
            // 404 means no integration exists - return null instead of throwing
            if (error instanceof ResourceNotFoundError) {
                return null;
            }
            // Re-throw other errors (network issues, 500s, etc.)
            throw error;
        }
    });
};

export const useDeleteEmailConnection = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(emailConnectionPath(companyId), async (path) => {
        await http.delete({
            path,
            accessToken: await getAccessTokenSilently(),
        });
    });
};

interface GetPersonalEmailSetupUrlRequest {
    emailProvider: EmailProvider;
}

interface GetPersonalEmailSetupUrlResponse {
    url: string;
}

export const useGetPersonalEmailSetupUrl = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        `company/${companyId}/settings/email-connection/auth-url`,
        async (path: string, { arg }: { arg: EmailProvider }) => {
            const payload: GetPersonalEmailSetupUrlRequest = {
                emailProvider: arg,
            };

            const response = await http.post<GetPersonalEmailSetupUrlResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
                body: payload,
            });

            return response;
        },
    );
};
